import { trackError } from '@billy/track-error'
import type { PeriodValue as LumenPeriodValue } from '@shinetools/lumen-react'

import { PeriodMode } from '../../../../enums/periodMode'
import type { PeriodValue } from '../../../../types/periodValue'

/**
 * Adapters between the app's `PeriodValue` (produced by `decodePeriodValue`, consumed by `encodePeriodValue`) and
 * the shape Lumen's `PeriodPicker` expects. They form an inverse pair — keep them in sync.
 *
 * Shape differences to be aware of:
 * - `quarter` is 1-indexed in Lumen and 0-indexed in the app.
 * - `month` uses different key names (`month` vs `value`).
 * - The app has a dedicated `All` mode; Lumen folds it into `custom` with both bounds null.
 * - The app splits custom dates across `CustomDate` / `CustomDateFrom` / `CustomDateThrough`; Lumen keeps a single
 *   `custom` mode with nullable bounds. `decodePeriodValue` already normalizes the split modes into `CustomDate`
 *   with a partial `customDate`, so `CustomDateFrom` / `CustomDateThrough` never reach `toLumenPeriodValue` in
 *   practice — we handle them for completeness.
 */

export const toLumenPeriodValue = (periodValue: PeriodValue | undefined): LumenPeriodValue | null => {
  if (!periodValue) {
    return null
  }

  switch (periodValue.mode) {
    case PeriodMode.Month:
      return periodValue.year !== undefined && periodValue.value !== undefined
        ? { mode: 'month', year: periodValue.year, month: periodValue.value }
        : null

    case PeriodMode.Quarter:
      return periodValue.year !== undefined && periodValue.value !== undefined
        ? { mode: 'quarter', year: periodValue.year, quarter: periodValue.value + 1 }
        : null

    case PeriodMode.FiscalYear:
      return periodValue.year !== undefined ? { mode: 'fiscalYear', year: periodValue.year } : null

    case PeriodMode.CustomDate:
    case PeriodMode.CustomDateFrom:
    case PeriodMode.CustomDateThrough:
      return {
        mode: 'custom',
        start: periodValue.customDate?.startDate ?? null,
        end: periodValue.customDate?.endDate ?? null,
      }

    case PeriodMode.All:
    default:
      return { mode: 'custom', start: null, end: null }
  }
}

export const fromLumenPeriodValue = (value: LumenPeriodValue | null): PeriodValue | undefined => {
  if (!value) {
    return undefined
  }

  switch (value.mode) {
    case 'month':
      return { mode: PeriodMode.Month, year: value.year, value: value.month }

    case 'quarter':
      return { mode: PeriodMode.Quarter, year: value.year, value: value.quarter - 1 }

    case 'fiscalYear':
      return { mode: PeriodMode.FiscalYear, year: value.year, value: value.year }

    case 'custom':
      if (!value.start && !value.end) {
        return { mode: PeriodMode.All }
      }

      return {
        mode: PeriodMode.CustomDate,
        customDate: {
          startDate: value.start ?? undefined,
          endDate: value.end ?? undefined,
        },
      }

    default:
      // Should be unreachable given Lumen's current `PeriodPickerValue` union — guard against a future Lumen
      // release adding a mode we haven't mapped yet, rather than silently dropping the change.
      trackError(new Error(`Unhandled Lumen period mode: ${(value as { mode: string }).mode}`))

      return undefined
  }
}
